import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { useEffect, useRef, useState, memo } from "react";
import type { FC } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall } from "../../multimodal-live-types";

// Function Declarations
const functionDeclarations = {
  renderGraph: {
    name: "render_molly",
    description: "Displays a graph in json format.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        json_graph: {
          type: SchemaType.STRING,
          description: "JSON STRING representation of the graph to render. Must be a string, not a json object",
        },
      },
      required: ["json_graph"],
    },
  },

  renderLLMResponse: {
    name: "render_llm_response",
    description: "Displays an LLM response which can be text or an image.",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        content: {
          type: SchemaType.STRING,
          description: "Content to render. Can be text or a base64 encoded image",
        },
        type: {
          type: SchemaType.STRING,
          enum: ["text", "image"],
          description: "Type of content being rendered",
        },
      },
      required: ["content", "type"],
    },
  },

  manageNotes: {
    name: "manage_notes",
    description: "Create, read, update, or delete notes in the CRM",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["create", "get", "update", "delete"],
          description: "Action to perform on notes"
        },
        id: {
          type: SchemaType.STRING,
          description: "Note ID (required for update/delete/get specific note)"
        },
        contact_id: {
          type: SchemaType.STRING,
          description: "Contact ID (required for create)"
        },
        body: {
          type: SchemaType.STRING,
          description: "Content of the note"
        },
        is_favorited: {
          type: SchemaType.BOOLEAN,
          description: "Whether the note is favorited"
        }
      },
      required: ["action"]
    }
  },

  manageTasks: {
    name: "manage_tasks",
    description: "Create, read, update, or delete tasks in the CRM",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["create", "get", "update", "delete"],
          description: "Action to perform on tasks"
        },
        id: {
          type: SchemaType.STRING,
          description: "Task ID (required for update/delete/get specific task)"
        },
        contact_id: {
          type: SchemaType.STRING,
          description: "Contact ID associated with the task"
        },
        title: {
          type: SchemaType.STRING,
          description: "Title of the task"
        },
        description: {
          type: SchemaType.STRING,
          description: "Description of the task"
        },
        completed: {
          type: SchemaType.BOOLEAN,
          description: "Whether the task is completed"
        },
        completed_at: {
          type: SchemaType.STRING,
          description: "When the task was completed (ISO 8601 format)"
        }
      },
      required: ["action"]
    }
  },

  crmSearch: {
    name: "crm_search",
    description: "Search for CRM records",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        objectType: {
          type: SchemaType.STRING,
          enum: ["contacts", "companies", "deals"],
          description: "Type of CRM object to search"
        },
        query: {
          type: SchemaType.STRING,
          description: "Search query string"
        },
        properties: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.STRING
          },
          description: "Properties to return in results"
        }
      },
      required: ["objectType", "query"]
    }
  },

  sendEmail: {
    name: "send_email",
    description: "Send an email through the system",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        to: {
          type: SchemaType.STRING,
          description: "Recipient email address"
        },
        subject: {
          type: SchemaType.STRING,
          description: "Email subject line"
        },
        html: {
          type: SchemaType.STRING,
          description: "Email body content"
        },
        priority: {
          type: SchemaType.STRING,
          enum: ["high", "normal", "low"],
          description: "Email priority level"
        }
      },
      required: ["to", "subject", "html"]
    }
  },

  manageCalendar: {
    name: "manage_calendar",
    description: "Get available slots and book meetings using Cal.com",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["get_slots", "book_meeting"],
          description: "Action to perform with the calendar"
        },
        eventTypeId: {
          type: SchemaType.STRING,
          description: "Cal.com event type ID"
        },
        startTime: {
          type: SchemaType.STRING,
          description: "Start time for slot availability (ISO 8601 format)"
        },
        endTime: {
          type: SchemaType.STRING,
          description: "End time for slot availability (ISO 8601 format)"
        },
        timeZone: {
          type: SchemaType.STRING,
          description: "Timezone for the meeting (e.g., 'America/New_York')"
        },
        name: {
          type: SchemaType.STRING,
          description: "Name of the person booking the meeting"
        },
        email: {
          type: SchemaType.STRING,
          description: "Email of the person booking the meeting"
        },
        notes: {
          type: SchemaType.STRING,
          description: "Additional notes for the meeting"
        },
        language: {
          type: SchemaType.STRING,
          description: "Language preference (default: 'en')"
        },
        guests: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.STRING
          },
          description: "List of guest email addresses"
        },
        rescheduleUid: {
          type: SchemaType.STRING,
          description: "UID of meeting to reschedule"
        }
      },
      required: ["action", "eventTypeId", "timeZone"]
    }
  },

  manageMauticContacts: {
    name: "manage_mautic_contacts",
    description: "Create, read, update, or search contacts in Mautic",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["create", "get", "update", "search"],
          description: "Action to perform on contacts"
        },
        id: {
          type: SchemaType.STRING,
          description: "Contact ID (required for update/get specific contact)"
        },
        data: {
          type: SchemaType.OBJECT,
          description: "Contact data for create/update operations",
          properties: {
            firstname: { type: SchemaType.STRING },
            lastname: { type: SchemaType.STRING },
            email: { type: SchemaType.STRING },
            phone: { type: SchemaType.STRING },
            company: { type: SchemaType.STRING },
            position: { type: SchemaType.STRING }
          }
        },
        query: {
          type: SchemaType.STRING,
          description: "Search query for contacts"
        }
      },
      required: ["action"]
    }
  },

  manageMauticSegments: {
    name: "manage_mautic_segments",
    description: "Create and retrieve segments in Mautic",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["create", "list"],
          description: "Action to perform on segments"
        },
        data: {
          type: SchemaType.OBJECT,
          description: "Segment data for create operation",
          properties: {
            name: { type: SchemaType.STRING },
            description: { type: SchemaType.STRING },
            isPublished: { type: SchemaType.BOOLEAN }
          }
        }
      },
      required: ["action"]
    }
  },

  manageMauticCampaigns: {
    name: "manage_mautic_campaigns",
    description: "Create and manage campaigns in Mautic",
    parameters: {
      type: SchemaType.OBJECT,
      properties: {
        action: {
          type: SchemaType.STRING,
          enum: ["create", "list", "add_contact"],
          description: "Action to perform on campaigns"
        },
        id: {
          type: SchemaType.STRING,
          description: "Campaign ID (required for add_contact)"
        },
        contactId: {
          type: SchemaType.STRING,
          description: "Contact ID to add to campaign"
        },
        data: {
          type: SchemaType.OBJECT,
          description: "Campaign data for create operation",
          properties: {
            name: { type: SchemaType.STRING },
            description: { type: SchemaType.STRING },
            isPublished: { type: SchemaType.BOOLEAN }
          }
        }
      },
      required: ["action"]
    }
  }
};

const MollyComponent: FC = () => {
  const [jsonString, setJSONString] = useState<string>("");
  const [llmResponse, setLLMResponse] = useState<{ content: string; type: "text" | "image" } | null>(null);
  const { client, setConfig } = useLiveAPIContext();
  const [isTextInput, setIsTextInput] = useState(false);

  // Set initial config for Molly
  useEffect(() => {
    const instructions = `System settings:
    Tool use: enabled.
    Instructions:
    - You are Molly, an AI assistant specialized in CRM management, email communications, and relationship maintenance
    - Your primary functions include managing contacts, tasks, notes, calendar, and email communications
    - You have direct access to the CRM system - use it proactively to look up information without asking the user
    - When given a name or context about a person, automatically search the CRM to gather relevant information
    - Make informed assumptions based on context and available data, you are based in Boston MA (America/New_York)
    - Only ask questions when critical information is missing and cannot be inferred or found in the CRM
    - Utilize the calendar to schedule appointments and events
    - Always say phone numbers in proper phone number format

    Response Guidelines:
    - Be concise and direct in your responses
    - Never repeat the user's question or input
    - Provide immediate, actionable responses
    - When searching for contacts, do it silently without mentioning the search process
    - Format responses in a clear, structured manner
    - Include relevant contact details and history when discussing a person
    - Proactively suggest next actions based on context

    Personality:
    - Your name is Molly
    - Professional yet warm
    - Efficient and precise
    - Confident in making informed decisions
    - Enthusiastic but focused
    - Solution-oriented
    - Speaks quickly

    Tool Usage:
    - Utilize CRM search proactively for contact information
    - Use CRM search to find notes and tasks
    - Contact ID is necessary for many actions, find Contact ID proactively using CRM search
    - Manage tasks and notes automatically when context implies the need
    - Schedule calendar events efficiently using available slots
    - Send emails when explicitly requested or clearly implied
    `;

    setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: isTextInput ? "text" : "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Kore" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: instructions,
          },
        ],
      },
      tools: [
        { googleSearch: {} },
        { 
          functionDeclarations: Object.values(functionDeclarations)
        },
      ],
    });
  }, [setConfig, isTextInput]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      console.log(`Molly is taking an action: `, toolCall);
      
      for (const fc of toolCall.functionCalls) {
        switch (fc.name) {
          case functionDeclarations.renderGraph.name:
            const str = (fc.args as any).json_graph;
            setJSONString(str);
            break;
            
          case functionDeclarations.renderLLMResponse.name:
            const { content, type } = fc.args as any;
            setLLMResponse({ content, type });
            break;
            
          case 'set_memory':
            handleMemoryOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageNotes.name:
            handleNotesOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageTasks.name:
            handleTasksOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.crmSearch.name:
            handleCRMSearch(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.sendEmail.name:
            handleSendEmail(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageCalendar.name:
            handleCalendarOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageMauticContacts.name:
            handleMauticContactsOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageMauticSegments.name:
            handleMauticSegmentsOperation(fc.args as any, fc.id);
            break;
            
          case functionDeclarations.manageMauticCampaigns.name:
            handleMauticCampaignsOperation(fc.args as any, fc.id);
            break;
        }
      }
    };
    
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);


  // Memory operation handler
  const handleMemoryOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, key, value } = args;
      let response: any = { success: true };
      
      switch (action) {
        case 'set':
          localStorage.setItem(key, value);
          break;
        case 'get':
          response.value = localStorage.getItem(key);
          break;
        case 'delete':
          localStorage.removeItem(key);
          break;
      }
      
      client.sendToolResponse({
        functionResponses: [{
          response: { output: response },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Notes operation handler
  const handleNotesOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, id, contact_id, body, is_favorited } = args;
      let url = '/api/notes';
      let method = 'GET';
      let requestBody = null;

      switch (action) {
        case 'create':
          method = 'POST';
          requestBody = { contact_id, body, is_favorited };
          break;
        case 'update':
          if (!id) throw new Error('Note ID is required for update');
          url = `/api/notes/${id}`;
          method = 'PUT';
          requestBody = { contact_id, body, is_favorited };
          break;
        case 'delete':
          if (!id) throw new Error('Note ID is required for delete');
          url = `/api/notes/${id}`;
          method = 'DELETE';
          break;
        case 'get':
          if (id) {
            url = `/api/notes/${id}`;
          }
          break;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && { body: JSON.stringify(requestBody) })
      });
      
      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Tasks operation handler
  const handleTasksOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, id, contact_id, title, description, completed, completed_at } = args;
      let url = '/api/tasks';
      let method = 'GET';
      let requestBody = null;

      switch (action) {
        case 'create':
          method = 'POST';
          requestBody = { contact_id, title, description, completed, completed_at };
          break;
        case 'update':
          if (!id) throw new Error('Task ID is required for update');
          url = `/api/tasks/${id}`;
          method = 'PUT';
          requestBody = { contact_id, title, description, completed, completed_at };
          break;
        case 'delete':
          if (!id) throw new Error('Task ID is required for delete');
          url = `/api/tasks/${id}`;
          method = 'DELETE';
          break;
        case 'get':
          if (id) {
            url = `/api/tasks/${id}`;
          }
          break;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && { body: JSON.stringify(requestBody) })
      });
      
      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // CRM search handler
  const handleCRMSearch = async (args: any, functionCallId: string) => {
    try {
      const { query, properties } = args;
      const response = await fetch('/api/crm/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          filters: {
            ...(properties?.dateRange && { dateRange: properties.dateRange })
          }
        })
      });
      
      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Email handler
  const handleSendEmail = async (args: any, functionCallId: string) => {
    try {
      const { to, subject, html, priority } = args;
      const response = await fetch('/api/mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, html, priority })
      });
      
      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Calendar operation handler
  const handleCalendarOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, eventTypeId, startTime, endTime, timeZone, name, email, notes, language, guests, rescheduleUid } = args;
      let url = '/api/calendar';
      let method = 'POST';
      let requestBody: any = { eventTypeId, timeZone };

      switch (action) {
        case 'get_slots':
          url += '/slots';
          requestBody = {
            ...requestBody,
            startTime,
            endTime
          };
          break;
        case 'book_meeting':
          url += '/book';
          requestBody = {
            ...requestBody,
            start: startTime,
            end: endTime,
            name,
            email,
            ...(notes && { notes }),
            ...(language && { language }),
            ...(guests && { guests }),
            ...(rescheduleUid && { rescheduleUid })
          };
          break;
        default:
          throw new Error(`Unknown calendar action: ${action}`);
      }

      console.log(`Calendar operation: ${action}`, requestBody);

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Calendar operation failed: ${action}`);
      }

      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      console.error('Calendar operation error:', error);
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Mautic Contacts operation handler
  const handleMauticContactsOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, id, data, query } = args;
      let url = '/api/mautic/contacts';
      let method = 'GET';
      let requestBody = null;

      switch (action) {
        case 'create':
          method = 'POST';
          requestBody = data;
          break;
        case 'update':
          if (!id) throw new Error('Contact ID is required for update');
          url = `/api/mautic/contacts/${id}`;
          method = 'PUT';
          requestBody = data;
          break;
        case 'search':
          url += '/search';
          requestBody = { query };
          break;
        case 'get':
          if (id) {
            url = `/api/mautic/contacts/${id}`;
          }
          break;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && { body: JSON.stringify(requestBody) })
      });
      
      const result = await response.json();
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Mautic Segments operation handler
  const handleMauticSegmentsOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, data } = args;
      let url = '/api/mautic/segments';
      let method = 'GET';
      let requestBody = null;

      switch (action) {
        case 'create':
          method = 'POST';
          requestBody = data;
          break;
        case 'list':
          break;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && { body: JSON.stringify(requestBody) })
      });
      
      const result = await response.json();
      console.log(result);
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  // Mautic Campaigns operation handler
  const handleMauticCampaignsOperation = async (args: any, functionCallId: string) => {
    try {
      const { action, id, contactId, data } = args;
      let url = '/api/mautic/campaigns';
      let method = 'GET';
      let requestBody = null;

      switch (action) {
        case 'create':
          method = 'POST';
          requestBody = data;
          break;
        case 'list':
          break;
        case 'add_contact':
          if (!id || !contactId) throw new Error('Campaign ID and Contact ID are required for add_contact');
          url = `/api/mautic/campaigns/${id}/contacts`;
          method = 'POST';
          requestBody = { contactId };
          break;
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && { body: JSON.stringify(requestBody) })
      });
      
      const result = await response.json();
      console.log(result);
      client.sendToolResponse({
        functionResponses: [{
          response: { output: result },
          id: functionCallId
        }]
      });
    } catch (error) {
      client.sendToolResponse({
        functionResponses: [{
          response: { error: (error as Error).message },
          id: functionCallId
        }]
      });
    }
  };

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString));
    }
  }, [embedRef, jsonString]);

  const renderResponse = (): JSX.Element | null => {
    if (llmResponse) {
      if (llmResponse.type === "image") {
        return <img src={`data:image/png;base64,${llmResponse.content}`} alt="LLM generated" className="max-w-full" />;
      } else {
        return <div className="whitespace-pre-wrap">{llmResponse.content}</div>;
      }
    }
    return null;
  };

  return (
    <div>
      <div className="vega-embed" ref={embedRef} />
      {renderResponse()}
    </div>
  );
}

export const Molly = memo(MollyComponent);